/**
 * おすすめ求人（Workgate + Carat 混在）の API クライアント。
 *
 * 画面から axios を生で叩かず、エンドポイント定義と Body の組み立てをここに集約する
 * （CLAUDE.md: HTTP / API Client Constraints, Infra Plugin Access Constraints）。
 * 新規エンドポイント `get_recommend_jobs/` は JSON。応募・詳細は既存エンドポイントだが、
 * サーバ側が JSON / FormData 両対応になっているため新規呼び出しは JSON で送る。
 */
import axios from 'axios'

const ENDPOINT_PREFIX = process.env.VUE_APP_ENDPOINT_PREFIX || '/'

/** サーバーの最悪応答（Workgate 8s×3 と Carat 25s の並列）を超える値にする */
const RECOMMEND_TIMEOUT_MS = 40000
const APPLY_TIMEOUT_MS = 60000
const DETAIL_TIMEOUT_MS = 30000

const url = (path: string) => `${ENDPOINT_PREFIX}${path}`

const postJson = <T>(path: string, body: unknown, timeout: number) =>
  axios.post<T>(url(path), body, {
    timeout,
    headers: { 'Content-Type': 'application/json' }
  })

type RecommendLayout = 'mypage' | 'dialog'

export interface RecommendJobsRequest {
  uuid: string
  format_id: string
  layout: RecommendLayout
  address?: string
  occupation?: string
  keyword?: string
  area?: string
  device_flg?: string
  app_flag?: string
  site_flag?: string
}

interface RecommendOfferDto {
  provider: string
  job_id: string
  title: string
  company_name: string
  pref_name: string
  city_name: string
  location_text: string
  salary_text: string
  employment_types: string[]
  features: string[]
  image_url: string
  snippet: string
  detail_url: string
  apply_url: string
  search_id: string
  is_applied: boolean
  apply_enabled: boolean
  match_score: number
}

interface RecommendProviderGroupDto {
  provider: string
  avg_score: number
  offers: RecommendOfferDto[]
}

export interface RecommendJobsResponse {
  providers: RecommendProviderGroupDto[]
  keyword: string
  keyword_tier: number | null
  layout: string
  search_id: string
}

/** 提供元をまたいだおすすめ求人を取得する */
export async function fetchRecommendJobs(
  payload: RecommendJobsRequest
): Promise<RecommendJobsResponse> {
  const res = await postJson<RecommendJobsResponse>(
    'get_recommend_jobs/',
    payload,
    RECOMMEND_TIMEOUT_MS
  )
  const data = res.data || ({} as RecommendJobsResponse)
  return {
    providers: Array.isArray(data.providers) ? data.providers : [],
    keyword: data.keyword || '',
    keyword_tier: data.keyword_tier ?? null,
    layout: data.layout || '',
    search_id: data.search_id || ''
  }
}

export interface CaratApplyRequest {
  uuid: string
  job_id: string
  search_id: string
  format_id: string
  /** 応募者の区分。求人の雇用形態ではない（toApplicantOccupation を使う） */
  occupation: string
  /** 一覧応募は ''、詳細応募は 'd'（既存の集計規約） */
  detail_flag: '' | 'd'
  keyword: string
  area: string
  display_position: number
  block_index: number
  device_flg: string
  app_flag: string
  site_flag: string
}

export interface CaratApplyResponse {
  result?: string
  message?: string
  /** 提供元が返した失敗理由（例: DUPLICATE_APPLICATION）。成功時は無い */
  error_code?: string
  /** 氏名にテスト語が含まれる会員は送信されずこのフラグが立つ */
  test?: string
}

/**
 * 応募APIの失敗レスポンスから提供元の失敗理由を取り出す。
 *
 * varuna は応募失敗を **HTTP 400** で返すため axios が例外を投げる。
 * 呼び出し側の `res.message` を読む分岐には到達しないので、
 * 本文は catch 側からこの関数で取り出す。
 */
export function extractApplyError(e: unknown): CaratApplyResponse {
  const data = (e as { response?: { data?: CaratApplyResponse } })?.response
    ?.data
  return data && typeof data === 'object' ? data : {}
}

export async function applyCaratJob(
  payload: CaratApplyRequest
): Promise<CaratApplyResponse> {
  const res = await postJson<CaratApplyResponse>(
    'application_carat/',
    payload,
    APPLY_TIMEOUT_MS
  )
  return res.data || {}
}

export interface WorkgateApplyRequest {
  uuid: string
  offer_id: string
  display_position: number
  block_index: number
  email: string
  lastname: string
  firstname: string
  lastname_kana: string
  firstname_kana: string
  birth: string
  pref_code: number | string
  address1: string
  gender: number | string
  environment: unknown
  telephone?: string
  zip_code?: string
  address2?: string
  education?: unknown
  job_history?: unknown
  current_job?: unknown
  introduction?: unknown
  format_id?: string
  keyword?: string
  area?: string
  device_flg?: string
  app_flag?: string
  site_flag?: string
}

export interface WorkgateApplyResponse {
  apply_id?: string
  error?: string
}

export async function applyWorkgateJob(
  payload: WorkgateApplyRequest
): Promise<WorkgateApplyResponse> {
  const res = await postJson<WorkgateApplyResponse>(
    'apply_workgate/',
    payload,
    APPLY_TIMEOUT_MS
  )
  return res.data || {}
}

/** Workgate の求人詳細。レスポンスは `{item: {...}}` か生オブジェクト */
export async function fetchWorkgateDetail(
  offerId: string
): Promise<Record<string, unknown> | null> {
  const res = await postJson<{
    item?: Record<string, unknown>
    error?: string
  }>('get_workgate_detail/', { offer_id: String(offerId) }, DETAIL_TIMEOUT_MS)
  const raw = res.data
  if (!raw) return null
  const detail =
    (raw.item as Record<string, unknown>) || (raw as Record<string, unknown>)
  if (!detail || detail.error) return null
  return detail
}
